import ImportModal from '../../components/shared/ImportModal';
import uploadService from '../../services/uploadService';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { User } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Store, Plus, RefreshCw, Loader2, Search, X,
  CheckCircle, AlertCircle, Clock, XCircle,
  Building2, Mail, Phone, Hash,
  Pencil, UserPlus, AlertTriangle, Trash2,
  Plug, ChevronRight, ChevronDown, ChevronUp, ArrowUpDown, Check, ArrowRight,
  ShieldCheck, Filter, Inbox, LayoutGrid, List,
  ExternalLink, MoreHorizontal, MoreVertical, Globe, Users, Upload,
  Download, BarChart3, Bell, Smartphone, MessageSquare,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import FooterBar from '../../components/shared/FooterBar';
import AmazonConnectModal from '../../components/Admin/SellerAccounts/AmazonConnectModal';
import FlipkartConnectModal from '../../components/Admin/SellerAccounts/FlipkartConnectModal';
import MeeshoConnectModal from '../../components/Admin/SellerAccounts/MeeshoConnectModal';
import { sellerService } from '../../services/sellerService';
import { sellerAccountService } from '../../services/sellerAccountService';
import { useSeller } from '../../context/SellerContext';
import { useAuth } from '../../context/AuthContext';
import { useSellers } from '../../hooks/useSellers';
import { MdOutlinePerson } from 'react-icons/md';
import UserListView from '../../components/Admin/UserManagement/UserListView';

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM SVG ICONS
   ═══════════════════════════════════════════════════════════════════════════ */

function AmazonIcon({ size = 18 }) {
  return <img src="/assets/marketplaces/amazon-icon.png" alt="Amazon" width={size} height={size} className="object-contain" />;
}

function FlipkartIcon({ size = 18 }) {
  return <img src="/assets/marketplaces/flipkart-icon.png" alt="Flipkart" width={size} height={size} className="object-contain" />;
}

function MeeshoIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#6D2077"/>
      <path d="M7 8.5C7 7.672 7.672 7 8.5 7s1.5.672 1.5 1.5V16H7V8.5zM11 10.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5V16h-3v-5.5zM15 8.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5V16h-3V8.5z" fill="white"/>
    </svg>
  );
}

const PlatformIcon = ({ platform, size = 18 }) => {
  switch (platform) {
    case 'AMAZON': return <AmazonIcon size={size} />;
    case 'FLIPKART': return <FlipkartIcon size={size} />;
    case 'MEESHO': return <MeeshoIcon size={size} />;
    default: return <Globe size={size} className="text-slate-400" />;
  }
};

/* ═══════════════════════════════════════════════════════════════════════════
   CONFIG
   ═══════════════════════════════════════════════════════════════════════════ */

const PLATFORM_META = {
  AMAZON:   { label: 'Amazon',   accent: '#f08804', accentLight: 'rgba(255,153,0,0.08)',   border: 'rgba(255,153,0,0.25)', text: 'text-amber-700',  bg: 'bg-amber-50',  ring: 'ring-amber-200/60' },
  FLIPKART: { label: 'Flipkart', accent: '#2874F0', accentLight: 'rgba(40,116,240,0.08)',  border: 'rgba(40,116,240,0.25)', text: 'text-[#C45500]',   bg: 'bg-[#FFF4E6]',   ring: 'ring-[#FFD89E]/60' },
  MEESHO:   { label: 'Meesho',   accent: '#6D2077', accentLight: 'rgba(109,32,119,0.08)',  border: 'rgba(109,32,119,0.25)', text: 'text-[#37475A]', bg: 'bg-[#F7F7F7]', ring: 'ring-[#D5D9D9]/60' },
};

const STATUS_META = {
  ACTIVE:    { label: 'Active',    dot: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', pulse: true },
  INACTIVE:  { label: 'Inactive',  dot: 'bg-slate-400',   bg: 'bg-slate-100',  text: 'text-slate-500',   border: 'border-slate-200',   pulse: false },
  SUSPENDED: { label: 'Suspended', dot: 'bg-red-500',     bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-200',     pulse: false },
};

const MARKETPLACE_OPTIONS = [
  { value: 'AMAZON', label: 'Amazon' },
  { value: 'FLIPKART', label: 'Flipkart' },
  { value: 'MEESHO', label: 'Meesho' },
];

const NOTIFICATION_OPTIONS = [
  { value: 'DAILY_ORDER_SALES', label: 'Daily Sales' },
  { value: 'REVIEW_REQUEST', label: 'Review Request' },
  { value: 'PRODUCT_LISTING_STATUS', label: 'Listing Status' },
  { value: 'FEEDBACK_REVIEW', label: 'Feedback Review' },
  { value: 'PRODUCT_HIJACK', label: 'Hijack Alert' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MODALS (ConfirmModal, AddSellerModal, ConnectPlatformPicker)
   ═══════════════════════════════════════════════════════════════════════════ */

function ConfirmModal({ isOpen, onClose, onConfirm, title, message, variant = 'warning', loading }) {
  if (!isOpen) return null;
  const colors = variant === 'danger'
    ? { icon: 'bg-red-100 text-red-600', btn: 'bg-red-600 hover:bg-red-700' }
    : { icon: 'bg-amber-100 text-amber-600', btn: 'bg-amber-600 hover:bg-amber-700' };
  const Icon = variant === 'danger' ? Trash2 : AlertTriangle;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="p-6">
          <div className={`w-12 h-12 rounded-xl ${colors.icon} flex items-center justify-center mb-4`}><Icon size={22} /></div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">{title}</h3>
          <p className="text-sm text-slate-500">{message}</p>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${colors.btn} disabled:opacity-50 flex items-center gap-2 transition-colors`}>
            {loading && <Loader2 size={14} className="animate-spin" />}Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AccountManagerStatsModal({ isOpen, onClose, onFilter, onFilterUnassigned }) {
  const [stats, setStats] = useState([]);
  const [unassigned, setUnassigned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('count'); // 'count' | 'name'
  const [sortDir, setSortDir] = useState('desc');   // 'asc' | 'desc'

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setSortKey('count');
    setSortDir('desc');
    setLoading(true);
    sellerService.getAccountManagerStats()
      .then((res) => {
        const data = res?.data ?? res ?? {};
        const managers = Array.isArray(data) ? data : (data.managers || []);
        setStats(Array.isArray(managers) ? managers : []);
        setUnassigned(Number(data.unassignedCount) || 0);
      })
      .catch(() => toast.error('Failed to load account manager stats'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };

  const maxCount = useMemo(
    () => stats.reduce((m, s) => Math.max(m, Number(s.sellerCount) || 0), 0),
    [stats],
  );

  const visibleStats = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q
      ? stats.filter(s => (s.accountManagerName || '').toLowerCase().includes(q)
          || (s.email || '').toLowerCase().includes(q))
      : [...stats];
    list.sort((a, b) => {
      let cmp;
      if (sortKey === 'name') {
        cmp = (a.accountManagerName || '').localeCompare(b.accountManagerName || '');
      } else {
        cmp = (Number(a.sellerCount) || 0) - (Number(b.sellerCount) || 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [stats, query, sortKey, sortDir]);

  if (!isOpen) return null;

  const totalSellers = stats.reduce((sum, s) => sum + (Number(s.sellerCount) || 0), 0);
  const SortIcon = ({ active }) => (
    <ArrowUpDown size={11} className={active ? 'text-[#C45500]' : 'text-slate-300'} />
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#FFF4E6] text-[#C45500] flex items-center justify-center">
              <BarChart3 size={18} />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-900">Account Manager Workload</h3>
              <p className="text-[12px] text-slate-500">Sellers assigned per account manager</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <X size={16} />
          </button>
        </div>

        {!loading && stats.length > 0 && (
          <div className="px-6 py-3 border-b border-slate-100">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter account managers…"
                className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] placeholder:text-slate-400"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100">
                  <X size={13} className="text-slate-400" />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="max-h-[55vh] overflow-y-auto">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-slate-400">
              <Loader2 size={28} className="animate-spin mb-2" />
              <p className="text-[13px]">Loading…</p>
            </div>
          ) : stats.length === 0 && unassigned === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-slate-400">
              <Users size={28} className="mb-2" />
              <p className="text-[13px]">No account managers assigned yet</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#F7F7F7] border-b border-[#D5D9D9] sticky top-0">
                <tr>
                  <th className="text-left py-2.5 px-5">
                    <button onClick={() => toggleSort('name')} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#565959] uppercase tracking-wider hover:text-[#C45500]">
                      Account Manager <SortIcon active={sortKey === 'name'} />
                    </button>
                  </th>
                  <th className="text-left text-[11px] font-semibold text-[#565959] uppercase tracking-wider py-2.5 px-4">Email</th>
                  <th className="text-right py-2.5 px-5">
                    <button onClick={() => toggleSort('count')} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#565959] uppercase tracking-wider hover:text-[#C45500]">
                      Sellers <SortIcon active={sortKey === 'count'} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleStats.length === 0 && query && (
                  <tr><td colSpan={3} className="py-10 text-center text-[13px] text-slate-400">No account managers match "{query}"</td></tr>
                )}
                {visibleStats.map((s) => {
                  const name = s.accountManagerName?.trim();
                  const count = Number(s.sellerCount) || 0;
                  const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
                  return (
                    <tr
                      key={s.accountManagerId}
                      onClick={() => name && onFilter?.(name)}
                      className="border-b border-slate-100 last:border-0 hover:bg-[#FFF8EF] cursor-pointer transition-colors"
                      title={`Filter sellers managed by ${name || 'this AM'}`}
                    >
                      <td className="py-2.5 px-5">
                        <p className="text-[13px] font-medium text-slate-800">{name || '—'}</p>
                        <div className="mt-1 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#f08804] rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-[12px] text-slate-500">{s.email || '—'}</td>
                      <td className="py-2.5 px-5 text-right align-top">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 bg-[#FFF4E6] text-[#C45500] text-[12px] font-bold rounded-md border border-[#FFD89E]">
                          {count}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {unassigned > 0 && !query && (
                  <tr
                    onClick={() => onFilterUnassigned?.()}
                    className="border-t-2 border-slate-200 hover:bg-amber-50/60 cursor-pointer transition-colors"
                    title="Filter sellers with no account manager"
                  >
                    <td className="py-2.5 px-5 text-[13px] font-semibold text-amber-700 flex items-center gap-1.5">
                      <AlertTriangle size={13} /> Unassigned
                    </td>
                    <td className="py-2.5 px-4 text-[12px] text-slate-400 italic">no account manager</td>
                    <td className="py-2.5 px-5 text-right">
                      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 bg-amber-50 text-amber-700 text-[12px] font-bold rounded-md border border-amber-200">
                        {unassigned}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {!loading && (stats.length > 0 || unassigned > 0) && (
          <div className="flex items-center justify-between px-6 py-3 bg-slate-50 border-t border-slate-100 text-[12px] text-slate-500">
            <span>{stats.length} account manager{stats.length === 1 ? '' : 's'} · {unassigned} unassigned</span>
            <span>{totalSellers} seller assignment{totalSellers === 1 ? '' : 's'}</span>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

const NOTIF_TYPE_LABELS = {
  DAILY_ORDER_SALES: 'Orders & Sales',
  PRODUCT_LISTING_STATUS: 'Product Listings',
  FEEDBACK_REVIEW: 'Reviews & Feedback',
  POLICY_WARNING: 'Policy Warning',
  SPONSORED_AD_PERFORMANCE: 'Ad Performance',
  PRODUCT_HIJACK: 'Hijack Alert',
  REVIEW_REQUEST: 'Review Request',
};

function NotificationCoverageModal({ isOpen, onClose, onFilterEnabled, onFilterDisabled }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    sellerService.getNotificationCoverage()
      .then((res) => setData(res?.data ?? res ?? null))
      .catch(() => toast.error('Failed to load notification coverage'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const total = Number(data?.totalSellers) || 0;
  const dark = Number(data?.darkSellers) || 0;
  const perType = Array.isArray(data?.perType) ? data.perType : [];

  // Build a row for every known type (+ any extra type the API returned),
  // with enabled count and the derived not-enabled count (total - enabled).
  const enabledByType = {};
  perType.forEach((t) => { enabledByType[t.type] = Number(t.sellerCount) || 0; });
  const allTypes = Array.from(
    new Set([...Object.keys(NOTIF_TYPE_LABELS), ...perType.map((t) => t.type)]),
  );
  const typeRows = allTypes.map((type) => {
    const enabled = enabledByType[type] || 0;
    return { type, label: NOTIF_TYPE_LABELS[type] || type, enabled, notEnabled: Math.max(total - enabled, 0) };
  });
  const enabledRows = [...typeRows].sort((a, b) => b.enabled - a.enabled);
  const disabledRows = [...typeRows].sort((a, b) => b.notEnabled - a.notEnabled);
  const maxType = typeRows.reduce((m, t) => Math.max(m, t.enabled), 0);
  const maxNotType = typeRows.reduce((m, t) => Math.max(m, t.notEnabled), 0);
  const cm = data?.channelMix || {};
  const channels = [
    { key: 'EMAIL', label: 'Email', icon: Mail, value: Number(cm.email ?? cm.EMAIL) || 0 },
    { key: 'WHATSAPP', label: 'WhatsApp', icon: Smartphone, value: Number(cm.whatsapp ?? cm.WHATSAPP) || 0 },
    { key: 'SMS', label: 'SMS', icon: MessageSquare, value: Number(cm.sms ?? cm.SMS) || 0 },
  ];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#FFF4E6] text-[#C45500] flex items-center justify-center">
              <Bell size={18} />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-900">Notification Coverage</h3>
              <p className="text-[12px] text-slate-500">How many sellers have notifications enabled</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-slate-400">
              <Loader2 size={28} className="animate-spin mb-2" />
              <p className="text-[13px]">Loading…</p>
            </div>
          ) : !data ? (
            <div className="py-16 flex flex-col items-center justify-center text-slate-400">
              <Bell size={28} className="mb-2" />
              <p className="text-[13px]">No data available</p>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {/* Callouts */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Sellers</p>
                  <p className="text-2xl font-bold text-slate-800 mt-0.5">{total}</p>
                </div>
                <div className={`rounded-xl border px-4 py-3 ${dark > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider ${dark > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>No Notifications</p>
                  <p className={`text-2xl font-bold mt-0.5 ${dark > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{dark}</p>
                </div>
              </div>

              {/* Per-type adoption — click to filter sellers WITH this enabled */}
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">Sellers per notification type</p>
                <p className="text-[11px] text-slate-400 mb-2 -mt-1.5">Click a row to view those sellers</p>
                <div className="space-y-1">
                  {enabledRows.map((t) => {
                    const pct = maxType > 0 ? Math.round((t.enabled / maxType) * 100) : 0;
                    return (
                      <button
                        key={t.type}
                        onClick={() => onFilterEnabled?.(t.type)}
                        className="w-full flex items-center gap-3 px-2 py-1 rounded-md hover:bg-[#FFF8EF] transition-colors text-left"
                        title={`View ${t.enabled} seller${t.enabled === 1 ? '' : 's'} with ${t.label} enabled`}
                      >
                        <span className="w-36 text-[12px] font-medium text-slate-700 truncate">{t.label}</span>
                        <div className="flex-1 h-4 bg-slate-100 rounded-md overflow-hidden">
                          <div className="h-full bg-[#f08804] rounded-md" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-10 text-right text-[12px] font-bold text-slate-700 tabular-nums">{t.enabled}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Gaps — click to filter sellers WITHOUT this enabled */}
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">Not enabled — coverage gaps</p>
                <p className="text-[11px] text-slate-400 mb-2 -mt-1.5">Sellers missing each notification — click to view them</p>
                <div className="space-y-1">
                  {disabledRows.map((t) => {
                    const pct = maxNotType > 0 ? Math.round((t.notEnabled / maxNotType) * 100) : 0;
                    return (
                      <button
                        key={t.type}
                        onClick={() => onFilterDisabled?.(t.type)}
                        className="w-full flex items-center gap-3 px-2 py-1 rounded-md hover:bg-amber-50 transition-colors text-left"
                        title={`View ${t.notEnabled} seller${t.notEnabled === 1 ? '' : 's'} without ${t.label} enabled`}
                      >
                        <span className="w-36 text-[12px] font-medium text-slate-700 truncate">{t.label}</span>
                        <div className="flex-1 h-4 bg-slate-100 rounded-md overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-md" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-10 text-right text-[12px] font-bold text-amber-700 tabular-nums">{t.notEnabled}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Channel mix */}
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">Sellers by active channel</p>
                <div className="grid grid-cols-3 gap-3">
                  {channels.map((c) => {
                    const Icon = c.icon;
                    return (
                      <div key={c.key} className="rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#FFF4E6] text-[#C45500] flex items-center justify-center flex-shrink-0">
                          <Icon size={15} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-lg font-bold text-slate-800 leading-none">{c.value}</p>
                          <p className="text-[11px] text-slate-400 mt-1">{c.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {!loading && data && (
          <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-[12px] text-slate-500">
            {total - dark} of {total} sellers have at least one notification enabled
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function AddSellerModal({ isOpen, onClose, onSave, editData }) {
  const [form, setForm] = useState({
    businessName: '', legalName: '', gstin: '', pan: '',
    contactEmail: '', contactPhone: '', address: '', notes: '',
    sellerUsername: '', sellerPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [createSellerUser, setCreateSellerUser] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm({
        businessName: editData.businessName || '',
        legalName: editData.legalName || '',
        gstin: editData.gstin || '',
        pan: editData.pan || '',
        contactEmail: editData.contactEmail || '',
        contactPhone: editData.contactPhone || '',
        address: editData.address || '',
        notes: editData.notes || '',
        sellerUsername: editData.sellerUsername || '',
        sellerPassword: editData.sellerPassword || '',
      });

      setCreateSellerUser(false);
    } else {
      setForm({
        businessName: '', legalName: '', gstin: '', pan: '',
        contactEmail: '', contactPhone: '', address: '', notes: '',
        sellerUsername: '', sellerPassword: '',
      });
      setCreateSellerUser(false);
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

 // Basic email shape check — kept lenient (matches "x@y.z" style) since browser
 // type="email" validation is loose. Adjust the regex if backend expects stricter.
 const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
 // Phone: accept 10-13 digits after stripping non-digits — covers plain Indian
 // 10-digit numbers and the +91-prefixed 12-digit form.
 const isValidPhone = (v) => {
   const digits = v.replace(/\D/g, '');
   return digits.length >= 10 && digits.length <= 13;
 };

 const handleSubmit = async (e) => {
   e.preventDefault();

   // All three required-field gates with friendly toasts
   if (!form.businessName.trim()) {
     toast.error('Business Name is required');
     return;
   }
   if (!form.contactEmail.trim()) {
     toast.error('Email is required');
     return;
   }
   if (!isValidEmail(form.contactEmail)) {
     toast.error('Please enter a valid email address');
     return;
   }
   if (!form.contactPhone.trim()) {
     toast.error('Phone is required');
     return;
   }
   if (!isValidPhone(form.contactPhone)) {
     toast.error('Please enter a valid phone number');
     return;
   }

   setSaving(true);
   try {
     const payload = { ...form };

     if (!createSellerUser || !payload.sellerUsername.trim()) delete payload.sellerUsername;
     if (!createSellerUser || !payload.sellerPassword.trim()) delete payload.sellerPassword;
     await onSave(payload);
   } finally {
     setSaving(false);
   }
 };


  const isEdit = !!editData;
  const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/30 focus:border-[#f08804] transition-all bg-white placeholder-slate-300";
  const labelCls = "block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1";

  const fillTestData = () => {
    const r = Math.floor(Math.random() * 9000) + 1000;
    setForm({
      businessName: `Test Seller ${r}`,
      legalName: `Test Seller ${r} Pvt Ltd`,
      gstin: `22ABCDE${r}0A1Z5`,
      pan: `ABCDE${r}F`,
      contactEmail: `seller${r}@test.com`,
      contactPhone: `98765${String(r).padStart(5, '0')}`,
      address: `${r} MG Road, Mumbai`,
      notes: 'Test seller for development',
      sellerUsername: `seller${r}`,
      sellerPassword: 'Test@1234',
    });
    setCreateSellerUser(true);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[820px] mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#f08804] to-[#e88b00] flex items-center justify-center shadow-sm">
              {isEdit ? <Pencil size={16} className="text-white" /> : <Store size={16} className="text-white" />}
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">{isEdit ? 'Edit Seller' : 'Add New Seller'}</h3>
              <p className="text-[11px] text-slate-400">{isEdit ? 'Update business information' : 'Onboard a new e-commerce seller'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEdit && (
              <button type="button" onClick={fillTestData} className="px-2.5 py-1.5 text-[10px] font-semibold text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors uppercase tracking-wider">
                Test Data
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
              <X size={16} className="text-slate-400" />
            </button>
          </div>
        </div>

        {/* Form Body — no scroll */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-3.5">

            {/* Row 1: Business Name, Legal Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Business Name <span className="text-red-500">*</span></label>
                <input type="text" value={form.businessName} onChange={(e) => setForm(f => ({ ...f, businessName: e.target.value }))} placeholder="e.g., Kaka Light & Sons" required className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Legal Name</label>
                <input type="text" value={form.legalName} onChange={(e) => setForm(f => ({ ...f, legalName: e.target.value }))} placeholder="Registered legal name" className={inputCls} />
              </div>
            </div>

            {/* Row 2: GSTIN, PAN, Email, Phone */}
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>GSTIN</label>
                <input type="text" value={form.gstin} onChange={(e) => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="22AAAAA0000A1Z5" maxLength={15} className={`${inputCls} font-mono`} />
              </div>
              <div>
                <label className={labelCls}>PAN</label>
                <input type="text" value={form.pan} onChange={(e) => setForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))} placeholder="ABCDE1234F" maxLength={10} className={`${inputCls} font-mono`} />
              </div>
            <div>
              <label className={labelCls}>Email <span className="text-red-500">*</span></label>
              <input type="email" value={form.contactEmail} onChange={(e) => setForm(f => ({ ...f, contactEmail: e.target.value }))} placeholder="seller@example.com" required className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone <span className="text-red-500">*</span></label>
              <input type="tel" value={form.contactPhone} onChange={(e) => setForm(f => ({ ...f, contactPhone: e.target.value }))} placeholder="+91 98765 43210" required className={inputCls} />
            </div>
            </div>

            {/* Row 3: Address, Notes */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Address</label>
                <input type="text" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Business address" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Notes</label>
                <input type="text" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Internal notes" className={inputCls} />
              </div>
            </div>

            {/* Seller User Account Toggle */}
            {!isEdit && (
              <div className="border border-slate-100 rounded-lg overflow-hidden">
                <label
                  className="flex items-center gap-3 px-3.5 py-2.5 cursor-pointer select-none group bg-slate-50/50 hover:bg-slate-50 transition-colors"
                  onClick={() => setCreateSellerUser(v => !v)}
                >
                  <div className={`w-[16px] h-[16px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    createSellerUser
                      ? 'bg-[#232F3E] border-[#C45500] shadow-sm shadow-[#FFD89E]'
                      : 'border-slate-300 bg-white group-hover:border-[#f08804]'
                  }`}>
                    {createSellerUser && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                  <p className="text-[12px] font-semibold text-slate-600">Create a login account for this seller</p>
                </label>

                {createSellerUser && (
                  <div className="px-3.5 pb-3 pt-2.5 border-t border-slate-100">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Username <span className="text-red-500">*</span></label>
                        <input type="text" value={form.sellerUsername} onChange={(e) => setForm(f => ({ ...f, sellerUsername: e.target.value }))} placeholder="e.g., kakauser" className={inputCls} autoComplete="off" />
                      </div>
                      <div>
                        <label className={labelCls}>Password <span className="text-red-500">*</span></label>
                        <input type="password" value={form.sellerPassword} onChange={(e) => setForm(f => ({ ...f, sellerPassword: e.target.value }))} placeholder="Strong password" className={inputCls} autoComplete="new-password" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2.5 px-2.5 py-1.5 bg-[#FFF9EE] border border-[#FFE8C8] rounded-md">
                      <ShieldCheck size={12} className="text-[#f08804] flex-shrink-0" />
                      <p className="text-[10px] text-[#C45500]">Share these credentials with the seller to enable platform login</p>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-3.5 border-t border-slate-100 bg-slate-50/50">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.businessName.trim() || !form.contactEmail.trim() || !form.contactPhone.trim()}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-[#232F3E] hover:bg-[#37475A] rounded-lg transition-colors disabled:opacity-50 shadow-sm"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Save Changes' : 'Add Seller'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function ConnectPlatformPicker({ isOpen, onClose, onSelect }) {
  if (!isOpen) return null;
  const platforms = [
    { key: 'AMAZON', label: 'Amazon SP-API', desc: 'Connect via Seller Central' },
    { key: 'FLIPKART', label: 'Flipkart', desc: 'Connect via Seller Dashboard' },
    { key: 'MEESHO', label: 'Meesho', desc: 'Connect via Supplier Panel' },
  ];
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Choose Platform</h3><p className="text-xs text-slate-500 mt-0.5">Select a marketplace to connect</p></div>
        <div className="p-3 space-y-2">
          {platforms.map(p => {
            const meta = PLATFORM_META[p.key];
            return (
              <button key={p.key} onClick={() => onSelect(p.key)} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-left group">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ backgroundColor: meta.accentLight, border: `1px solid ${meta.border}` }}>
                  <PlatformIcon platform={p.key} size={22} />
                </div>
                <div className="flex-1"><span className="text-sm font-semibold text-slate-800 block">{p.label}</span><span className="text-[11px] text-slate-400">{p.desc}</span></div>
                <ArrowRight size={14} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MULTI-SELECT DROPDOWN
   ═══════════════════════════════════════════════════════════════════════════ */

function MultiSelectDropdown({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (value) => {
    if (selected.includes(value)) onChange(selected.filter(v => v !== value));
    else onChange([...selected, value]);
  };

  const displayText = selected.length === 0
    ? label
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || label
      : `${selected.length} selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-2 px-3 py-[7px] border rounded-lg text-[13px] transition-all focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 whitespace-nowrap ${
          selected.length > 0
            ? 'border-[#FFD89E] bg-[#FFF4E6]/60 text-[#C45500] font-medium'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }`}
      >
        <span>{displayText}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''} ${selected.length > 0 ? 'text-[#f08804]' : 'text-slate-400'}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1.5 left-0 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[180px]">
          {options.map(opt => {
            const isSelected = selected.includes(opt.value);
            return (
              <button key={opt.value} type="button" onClick={() => toggle(opt.value)} className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors text-left">
                <span className={`w-4 h-4 flex items-center justify-center rounded-md border flex-shrink-0 transition-colors ${isSelected ? 'bg-[#232F3E] border-[#C45500]' : 'border-slate-300 bg-white'}`}>
                  {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
                </span>
                <span className="flex-1">{opt.label}</span>
              </button>
            );
          })}
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="w-full px-3.5 py-2 text-xs text-red-500 hover:bg-red-50 border-t border-slate-100 text-left transition-colors">Clear</button>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SKELETON LOADERS
   ═══════════════════════════════════════════════════════════════════════════ */

function SummaryStatCard({ label, value, icon: Icon, color = '#232F3E', active = false, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`bg-white rounded-lg border px-4 py-3 flex items-center gap-3 text-left transition-all w-full ${
        active
          ? 'border-[#f08804] ring-2 ring-[#f08804]/20 shadow-sm'
          : 'border-[#D5D9D9] hover:border-[#f08804]/40 hover:shadow-sm'
      }`}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: active ? `${color}20` : `${color}12` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-xl font-bold text-[#0F1111] leading-none">{value}</p>
        <p className="text-[11px] text-[#565959] mt-0.5">{label}</p>
      </div>
    </button>
  );
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 animate-pulse">
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-200" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 bg-slate-200 rounded-lg w-3/5" />
            <div className="h-2.5 bg-slate-100 rounded w-2/5" />
          </div>
        </div>
        <div className="flex gap-1.5">
          <div className="h-6 bg-slate-100 rounded-md w-16" />
          <div className="h-6 bg-slate-100 rounded-md w-16" />
        </div>
        <div className="h-px bg-slate-100" />
        <div className="flex justify-between">
          <div className="h-2.5 bg-slate-100 rounded w-24" />
          <div className="h-2.5 bg-slate-100 rounded w-10" />
        </div>
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden animate-pulse">
      <div className="p-4 space-y-0">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-4 border-b border-slate-50 last:border-0">
            <div className="w-9 h-9 rounded-lg bg-slate-200" />
            <div className="flex-1 space-y-1.5"><div className="h-3.5 bg-slate-200 rounded w-40" /><div className="h-3 bg-slate-100 rounded w-24" /></div>
            <div className="h-5 bg-slate-100 rounded-full w-16" />
            <div className="flex gap-1"><div className="h-6 bg-slate-100 rounded w-16" /><div className="h-6 bg-slate-100 rounded w-16" /></div>
            <div className="h-3 bg-slate-100 rounded w-32" />
            <div className="h-3 bg-slate-100 rounded w-28 font-mono" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONTEXT MENU (shared between Card and Table views)
   ═══════════════════════════════════════════════════════════════════════════ */

function SellerContextMenu({ seller, onEdit, onConnectChannel, onDelete, onAssignUsers }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const { isAdminOrManagement } = useAuth();
  const channels = seller.channels || [];
  const MENU_WIDTH = 208; // w-52

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const closeOnScroll = () => setOpen(false);
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnScroll);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', closeOnScroll);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_WIDTH),
    });
    setOpen(true);
  };

  return (
    <div className="relative" onClick={e => e.stopPropagation()}>
      <button
        ref={triggerRef}
        onClick={handleToggle}
        className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-colors shadow-sm"
        title="Actions"
      >
        <MoreVertical size={13} />
      </button>

      {open && (
        <div
          ref={menuRef}
          className="fixed w-52 bg-white rounded-xl border border-slate-200 shadow-xl z-[9999] overflow-hidden py-1"
          style={{ top: pos.top, left: pos.left }}
        >
          <button onClick={() => { onEdit(); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors text-left">
            <Pencil size={14} className="text-slate-400" />Edit Seller
          </button>
          <button onClick={() => { onConnectChannel(seller); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors text-left">
            <Globe size={14} className="text-slate-400" />Connect Marketplace
          </button>
          {isAdminOrManagement() && (
            <button onClick={() => { onAssignUsers(); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors text-left">
              <UserPlus size={14} className="text-slate-400" />Assign Member
            </button>
          )}
          {channels.length === 0 && (
            <>
              <div className="my-1 border-t border-slate-100" />
              <button onClick={() => { onDelete(seller); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-red-600 hover:bg-red-50 transition-colors text-left">
                <Trash2 size={14} className="text-red-400" />Delete Seller
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SELLER CARD (Card View)
   ═══════════════════════════════════════════════════════════════════════════ */

function SellerCard({ seller, onSelect, onEdit, onDelete, onConnectChannel, onRefresh, onAssignUsers, style }) {
  const channels = seller.channels || [];
  const status = STATUS_META[seller.status] || STATUS_META.INACTIVE;
  const uniquePlatforms = [...new Set(channels.map(c => c.platform))];
  const connectedCount = channels.filter(c => c.accountStatus === 'AUTHORIZED').length;

  return (
    <div style={style} className="group relative">
      <button
        onClick={onSelect}
        className="relative w-full bg-white rounded-lg border border-[#D5D9D9] text-left overflow-hidden transition-all duration-200 hover:shadow-md hover:border-[#f08804]/40 focus:outline-none focus:ring-2 focus:ring-[#f08804]/30 cursor-pointer"
      >
        <div className="p-4">
          {/* Header: Name + Status */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-10 h-10 rounded-md bg-[#232F3E] flex items-center justify-center text-[#f08804] font-bold text-sm flex-shrink-0">
                {seller.businessName?.charAt(0)?.toUpperCase() || 'S'}
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-[#0F1111] truncate group-hover:text-[#C45500] transition-colors">{seller.businessName}</h3>
                {seller.legalName && seller.legalName !== seller.businessName && (
                  <p className="text-[11px] text-[#565959] truncate">{seller.legalName}</p>
                )}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${status.bg} ${status.text} border ${status.border} flex-shrink-0`}>
              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          </div>

          {/* Marketplaces */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {uniquePlatforms.length > 0 ? uniquePlatforms.map((platform) => {
              const meta = PLATFORM_META[platform];
              const authorized = channels.filter(c => c.platform === platform).some(c => c.accountStatus === 'AUTHORIZED');
              return (
                <span key={platform} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#F7F7F7] border border-[#E7E7E7] text-[11px] font-medium text-[#0F1111]">
                  <PlatformIcon platform={platform} size={14} />
                  {meta.label}
                  {authorized ? <CheckCircle size={11} className="text-emerald-500" /> : <AlertCircle size={11} className="text-amber-500" />}
                </span>
              );
            }) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#F7F7F7] border border-[#E7E7E7] text-[11px] text-[#888]">
                <Plug size={11} /> No marketplaces
              </span>
            )}
          </div>

          {/* Account Manager */}
          {seller.accountManagerName ? (
            <div className="mb-2 text-[11px] text-[#565959] min-w-0">
              <div className="flex items-center gap-1.5 truncate">
                <User size={11} className="text-[#999] flex-shrink-0" />
                <span className="truncate font-medium text-[#0F1111]">{seller.accountManagerName}</span>
              </div>
              {(seller.accountManagerEmail || seller.accountManagerPhone) && (
                <div className="flex items-center gap-3 mt-0.5 pl-[18px] text-[10.5px] text-[#888] truncate">
                  {seller.accountManagerEmail && (
                    <span className="flex items-center gap-1 truncate" title={seller.accountManagerEmail}>
                      <Mail size={10} className="text-[#999] flex-shrink-0" />
                      <span className="truncate">{seller.accountManagerEmail}</span>
                    </span>
                  )}
                  {seller.accountManagerPhone && (
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <Phone size={10} className="text-[#999] flex-shrink-0" />
                      {seller.accountManagerPhone}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 mb-2 text-[11px] text-[#565959]">
              <User size={11} className="text-[#999] flex-shrink-0" />
              <span className="text-[#999] italic">No account manager</span>
            </div>
          )}

          {/* Footer: Contact + Channels count */}
          <div className="pt-2.5 border-t border-[#E7E7E7] flex items-center justify-between text-[11px] text-[#565959]">
            <div className="flex items-center gap-1 truncate">
              {seller.contactEmail ? (
                <><Mail size={11} className="text-[#999] flex-shrink-0" /><span className="truncate">{seller.contactEmail}</span></>
              ) : seller.contactPhone ? (
                <><Phone size={11} className="text-[#999] flex-shrink-0" /><span>{seller.contactPhone}</span></>
              ) : (
                <span className="text-[#999]">No contact info</span>
              )}
            </div>
            <span className="flex-shrink-0 font-medium">{connectedCount}/{channels.length} channels</span>
          </div>
        </div>
      </button>
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-150 z-10">
        <SellerContextMenu seller={seller} onEdit={onEdit} onConnectChannel={onConnectChannel} onDelete={onDelete} onAssignUsers={onAssignUsers} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SELLER TABLE ROW (Table View)
   ═══════════════════════════════════════════════════════════════════════════ */

function SellerTableRow({ seller, onSelect, onEdit, onDelete, onConnectChannel, onRefresh, onAssignUsers }) {
  const channels = seller.channels || [];
  const status = STATUS_META[seller.status] || STATUS_META.INACTIVE;
  const uniquePlatforms = [...new Set(channels.map(c => c.platform))];
  const connectedCount = channels.filter(c => c.accountStatus === 'AUTHORIZED').length;
  return (
    <tr className="group border-b border-[#E7E7E7] last:border-0 hover:bg-[#F7FAFA] cursor-pointer transition-colors" onClick={onSelect}>
      <td className="py-3 px-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#232F3E] flex items-center justify-center text-[#f08804] text-xs font-bold flex-shrink-0">
            {seller.businessName?.charAt(0)?.toUpperCase() || 'S'}
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-[#0F1111] truncate group-hover:text-[#C45500] transition-colors">{seller.businessName}</p>
            {seller.legalName && seller.legalName !== seller.businessName && <p className="text-[11px] text-[#565959] truncate">{seller.legalName}</p>}
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap gap-1">
          {uniquePlatforms.length > 0 ? uniquePlatforms.map((p) => {
            const meta = PLATFORM_META[p];
            const pChannels = channels.filter(c => c.platform === p);
            const authorized = pChannels.some(c => c.accountStatus === 'AUTHORIZED');
            return (
              <span key={p} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#F7F7F7] border border-[#E7E7E7] text-[11px] font-medium text-[#0F1111]">
                <PlatformIcon platform={p} size={14} />
                {meta.label}
                {authorized ? <CheckCircle size={11} className="text-emerald-500" /> : <AlertCircle size={11} className="text-amber-500" />}
              </span>
            );
          }) : (
            <span className="text-[11px] text-[#999]">No marketplaces</span>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="text-[12px] font-medium text-[#0F1111]">{connectedCount}<span className="text-[#999]">/{channels.length}</span></span>
      </td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${status.bg} ${status.text} border ${status.border}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
      </td>
      <td className="py-3 px-4">
        {seller.accountManagerName ? (
          <div className="text-[11px] max-w-[200px] min-w-0">
            <div className="flex items-center gap-1.5 truncate">
              <User size={11} className="text-[#999] flex-shrink-0" />
              <span className="truncate font-medium text-[#0F1111]">{seller.accountManagerName}</span>
            </div>
            {(seller.accountManagerEmail || seller.accountManagerPhone) && (
              <div className="mt-0.5 pl-[18px] text-[10.5px] text-[#888] flex flex-col gap-0.5">
                {seller.accountManagerEmail && (
                  <span className="flex items-center gap-1 truncate" title={seller.accountManagerEmail}>
                    <Mail size={10} className="text-[#999] flex-shrink-0" />
                    <span className="truncate">{seller.accountManagerEmail}</span>
                  </span>
                )}
                {seller.accountManagerPhone && (
                  <span className="flex items-center gap-1">
                    <Phone size={10} className="text-[#999] flex-shrink-0" />
                    {seller.accountManagerPhone}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <span className="text-[#CCC] text-[11px]">—</span>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 text-[11px] text-[#565959] max-w-[180px]">
          {seller.contactEmail ? (
            <><Mail size={11} className="text-[#999] flex-shrink-0" /><span className="truncate">{seller.contactEmail}</span></>
          ) : seller.contactPhone ? (
            <><Phone size={11} className="text-[#999] flex-shrink-0" /><span>{seller.contactPhone}</span></>
          ) : (
            <span className="text-[#CCC]">—</span>
          )}
        </div>
      </td>
      <td className="py-3 px-4 whitespace-nowrap">
        {seller.createdDateTime ? (
          <span className="text-[11px] text-[#565959]" title={new Date(seller.createdDateTime).toLocaleString('en-IN')}>
            {new Date(seller.createdDateTime).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        ) : (
          <span className="text-[#CCC] text-[11px]">—</span>
        )}
      </td>
      <td className="py-3 px-3 w-[50px]">
        <div className="flex items-center gap-1">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <SellerContextMenu seller={seller} onEdit={onEdit} onConnectChannel={onConnectChannel} onDelete={onDelete} onAssignUsers={onAssignUsers} />
          </div>
          <ChevronRight size={14} className="text-[#CCC] group-hover:text-[#f08804] transition-colors" />
        </div>
      </td>
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SORTABLE COLUMN HEADER
   ═══════════════════════════════════════════════════════════════════════════ */

function SortableTh({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  const Icon = active ? (sortDir === 'asc' ? ChevronUp : ChevronDown) : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={`inline-flex items-center gap-1 transition-colors ${active ? 'text-[#0F1111]' : 'text-[#565959] hover:text-[#0F1111]'}`}
    >
      <span className="uppercase tracking-wider">{label}</span>
      <Icon size={12} className={active ? 'text-[#f08804]' : 'text-[#999]'} />
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FILTER STATE
   ═══════════════════════════════════════════════════════════════════════════ */

const emptyFilters = {
  search: '',
  status: '',
  channels: [],
  notificationConfig: [],
  pan: '',
  gstin: '',
  contactPhone: '',
  contactEmail: '',
  accountManager: '',
  amUnassigned: false,
  notifEnabledType: '',
  notifDisabledType: '',
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */

export default function SellerHubPage() {
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [amStatsOpen, setAmStatsOpen] = useState(false);
  const [notifCoverageOpen, setNotifCoverageOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();
  const { setSelectedSeller } = useSeller();
  const { isAdminOrManagement } = useAuth();

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await sellerService.exportSellers();
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sellers-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Seller export downloaded');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const am = searchParams.get('accountManager');
    return am ? { ...emptyFilters, accountManager: am } : emptyFilters;
  });
  // Drop the query param after seeding so a manual filter-clear sticks on refresh.
  useEffect(() => {
    if (searchParams.get('accountManager')) {
      const next = new URLSearchParams(searchParams);
      next.delete('accountManager');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [quickFilter, setQuickFilter] = useState(''); // 'active', 'connected', 'disconnected', ''
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('seller_hub_view') || 'table');
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  // Server-side sort: sortBy ∈ {'name','createdAt'}, sortDir ∈ {'asc','desc'}. Default name asc.
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const PAGE_SIZE = 20;

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const [debouncedAccountManager, setDebouncedAccountManager] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAccountManager(filters.accountManager), 400);
    return () => clearTimeout(timer);
  }, [filters.accountManager]);

  useEffect(() => { setCurrentPage(0); }, [debouncedSearch, debouncedAccountManager, filters.amUnassigned, filters.notifEnabledType, filters.notifDisabledType, filters.channels, filters.notificationConfig, filters.status, sortBy, sortDir]);

  const { sellers, loading, pagination, refetch } = useSellers({
    platforms: filters.channels,
    notificationTypes: filters.notificationConfig,
    search: debouncedSearch,
    managerName: filters.amUnassigned ? '__UNASSIGNED__' : debouncedAccountManager,
    notifEnabledType: filters.notifEnabledType,
    notifDisabledType: filters.notifDisabledType,
    sortBy,
    sortDir,
    page: currentPage,
    size: PAGE_SIZE,
  });

  // Click handler: toggle direction if same column, else switch column with sensible default direction.
  const handleSortClick = (col) => {
    if (sortBy === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      // Names default to asc (A→Z); dates default to desc (newest first).
      setSortDir(col === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const [addSellerModal, setAddSellerModal] = useState({ open: false, editData: null });
  const [platformPicker, setPlatformPicker] = useState({ open: false, sellerId: null });
  const [amazonModal, setAmazonModal] = useState({ open: false, sellerId: null });
  const [flipkartModal, setFlipkartModal] = useState({ open: false, sellerId: null });
  const [meeshoModal, setMeeshoModal] = useState({ open: false, sellerId: null });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', variant: 'warning', action: null });
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await refetch(); toast.success('Refreshed'); }
    finally { setRefreshing(false); }
  };

  const setView = (mode) => { setViewMode(mode); localStorage.setItem('seller_hub_view', mode); };

  const hasActiveFilters = useMemo(() =>
    quickFilter !== '' || Object.entries(filters).some(([, v]) =>
      Array.isArray(v) ? v.length > 0 : (typeof v === 'boolean' ? v : v !== ''))
  , [filters, quickFilter]);

  const filteredSellers = useMemo(() => {
    return sellers.filter(s => {
      const f = filters;
      if (f.status && s.status !== f.status) return false;
      if (f.pan && !(s.pan || '').toLowerCase().includes(f.pan.toLowerCase())) return false;
      if (f.gstin && !(s.gstin || '').toLowerCase().includes(f.gstin.toLowerCase())) return false;
      if (f.contactPhone && !(s.contactPhone || '').includes(f.contactPhone)) return false;
      if (f.contactEmail && !(s.contactEmail || '').toLowerCase().includes(f.contactEmail.toLowerCase())) return false;
      // Quick filters from stat cards
      if (quickFilter === 'active' && s.status !== 'ACTIVE') return false;
      if (quickFilter === 'inactive' && s.status === 'ACTIVE') return false;
      if (quickFilter === 'connected') {
        const hasConnected = (s.channels || []).some(c => c.accountStatus === 'AUTHORIZED');
        if (!hasConnected) return false;
      }
      if (quickFilter === 'disconnected') {
        const allConnected = (s.channels || []).length > 0 && (s.channels || []).every(c => c.accountStatus === 'AUTHORIZED');
        if (allConnected || (s.channels || []).length === 0) return false;
      }
      if (quickFilter === 'no_channels' && (s.channels || []).length > 0) return false;
      return true;
    });
  }, [sellers, filters, quickFilter]);

  const counts = useMemo(() => {
    const c = { total: pagination.totalElements || sellers.length, ACTIVE: 0, INACTIVE: 0, SUSPENDED: 0, channels: 0, connected: 0 };
    sellers.forEach(s => {
      if (c[s.status] !== undefined) c[s.status]++;
      (s.channels || []).forEach(ch => { c.channels++; if (ch.accountStatus === 'AUTHORIZED') c.connected++; });
    });
    return c;
  }, [sellers, pagination]);

  const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }));
  const handleResetFilters = () => { setFilters(emptyFilters); setQuickFilter(''); };

  const handleSelectSeller = (seller) => {
    setSelectedSeller({ ...seller });
    navigate('/dashboard');
  };

  const handleSaveSeller = async (formData) => {
    try {
      if (addSellerModal.editData) { await sellerService.update(addSellerModal.editData.id, formData); toast.success('Seller updated'); }
      else { await sellerService.create(formData); toast.success('Seller added'); }
      setAddSellerModal({ open: false, editData: null });
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to save seller'); }
  };

  const handleDeleteSeller = (seller) => {
    setConfirmModal({ open: true, variant: 'danger', title: 'Delete Seller', message: `Permanently delete "${seller.businessName}"? This cannot be undone.`, action: async () => { await sellerService.delete(seller.id); toast.success('Seller deleted'); refetch(); } });
  };

  const handleConnectChannel = (seller) => { setPlatformPicker({ open: true, sellerId: seller.id }); };

  const handlePlatformSelect = (platform) => {
    const sid = platformPicker.sellerId;
    setPlatformPicker({ open: false, sellerId: null });
    if (platform === 'AMAZON') setAmazonModal({ open: true, sellerId: sid });
    else if (platform === 'FLIPKART') setFlipkartModal({ open: true, sellerId: sid });
    else if (platform === 'MEESHO') setMeeshoModal({ open: true, sellerId: sid });
  };

  const handleConnect = async (data) => {
    if (data?._oauthComplete) { setAmazonModal({ open: false, sellerId: null }); refetch(); return; }
    try {
      await sellerAccountService.connect(data);
      toast.success('Channel connected');
      setAmazonModal({ open: false, sellerId: null }); setFlipkartModal({ open: false, sellerId: null }); setMeeshoModal({ open: false, sellerId: null });
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Connection failed'); }
  };

  const handleConfirm = async () => {
    if (!confirmModal.action) return;
    setConfirmLoading(true);
    try { await confirmModal.action(); } catch (err) { toast.error(err?.response?.data?.message || 'Action failed'); }
    finally { setConfirmLoading(false); setConfirmModal({ open: false, title: '', message: '', variant: 'warning', action: null }); }
  };

  const advInputCls = "px-3 py-[7px] border border-slate-200 rounded-lg text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] transition-all placeholder:text-slate-400";

  return (
    <AdminLayout>
      <div className="min-h-full flex flex-col">

        {/* ━━━ HEADER ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div className="bg-white border-b border-[#D5D9D9] px-4 sm:px-6 py-2 flex items-center justify-between sticky top-0 z-10">
          <h1 className="text-[14px] font-bold text-[#0F1111]">
            My Sellers {pagination.totalElements > 0 && <span className="font-normal text-[#565959]">({pagination.totalElements})</span>}
          </h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handleRefresh} disabled={refreshing} className="p-1.5 rounded bg-white border border-[#D5D9D9] text-[#565959] hover:bg-[#F7F7F7] disabled:opacity-50" title="Refresh">
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
            {isAdminOrManagement() && (
              <button onClick={() => setAmStatsOpen(true)} className="px-2.5 py-1.5 bg-white text-[#565959] border border-[#D5D9D9] rounded hover:bg-[#F7F7F7] flex items-center gap-1.5 text-[12px] font-medium transition-colors" title="Sellers per account manager">
                <BarChart3 size={13} /><span className="hidden sm:inline">AM Workload</span>
              </button>
            )}
            {isAdminOrManagement() && (
              <button onClick={() => setNotifCoverageOpen(true)} className="px-2.5 py-1.5 bg-white text-[#565959] border border-[#D5D9D9] rounded hover:bg-[#F7F7F7] flex items-center gap-1.5 text-[12px] font-medium transition-colors" title="Notification coverage across sellers">
                <Bell size={13} /><span className="hidden sm:inline">Coverage</span>
              </button>
            )}
            <button onClick={handleExport} disabled={exporting} className="px-2.5 py-1.5 bg-white text-[#565959] border border-[#D5D9D9] rounded hover:bg-[#F7F7F7] flex items-center gap-1.5 text-[12px] font-medium transition-colors disabled:opacity-50">
              {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              <span className="hidden sm:inline">Export</span>
            </button>
            <button onClick={() => setImportModalOpen(true)} className="px-2.5 py-1.5 bg-white text-[#C45500] border border-[#FFD89E] rounded hover:bg-[#FFF4E6] flex items-center gap-1.5 text-[12px] font-medium transition-colors">
              <Upload size={13} /><span className="hidden sm:inline">Import Sellers</span>
            </button>
            <button onClick={() => setAddSellerModal({ open: true, editData: null })} className="px-2.5 sm:px-3 py-1.5 bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] text-[#0F1111] rounded hover:from-[#f0c14b] hover:to-[#e7ae00] flex items-center gap-1.5 text-[12px] font-semibold shadow-sm">
              <Plus size={13} /><span className="hidden sm:inline">Add Seller</span>
            </button>
          </div>
          <ImportModal
            open={importModalOpen}
            onClose={() => setImportModalOpen(false)}
            onImport={(file) => uploadService.bulkUploadSellers(file)}
            onSuccess={() => { refetch(); toast.success('Seller list refreshed'); }}
            title="Import Sellers"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-3 sm:p-5 space-y-4">

        {/* ━━━ FILTER BAR ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {(sellers.length > 0 || hasActiveFilters) && (
          <div className="bg-white rounded-lg border border-[#D5D9D9]">
            <div className="px-4 py-2.5 flex flex-wrap items-center gap-2.5">
              <div className="relative flex-1 min-w-[150px] sm:min-w-[200px] max-w-sm">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search sellers..."
                  className="w-full pl-9 pr-8 py-[7px] border border-slate-200 rounded-lg text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] transition-all placeholder:text-slate-400"
                />
                {filters.search && (
                  <button onClick={() => handleFilterChange('search', '')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 transition-colors">
                    <X size={13} className="text-slate-400" />
                  </button>
                )}
              </div>
              <select
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
                className="px-3 py-[7px] border border-[#D5D9D9] rounded-lg text-[13px] bg-white text-[#0F1111] focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]"
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
              <MultiSelectDropdown label="Marketplaces" options={MARKETPLACE_OPTIONS} selected={filters.channels} onChange={vals => handleFilterChange('channels', vals)} />
              <MultiSelectDropdown label="Notifications" options={NOTIFICATION_OPTIONS} selected={filters.notificationConfig} onChange={vals => handleFilterChange('notificationConfig', vals)} />
              <div className="relative">
                <User size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={filters.amUnassigned ? 'Unassigned (no AM)' : filters.accountManager}
                  readOnly={filters.amUnassigned}
                  onChange={e => setFilters(f => ({ ...f, accountManager: e.target.value, amUnassigned: false }))}
                  placeholder="Account Manager"
                  className={`pl-7 pr-7 py-[7px] border border-[#D5D9D9] rounded-lg text-[13px] bg-white text-[#0F1111] focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] placeholder:text-slate-400 w-[170px] ${filters.amUnassigned ? 'italic text-amber-700' : ''}`}
                />
                {(filters.accountManager || filters.amUnassigned) && (
                  <button onClick={() => setFilters(f => ({ ...f, accountManager: '', amUnassigned: false }))} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 transition-colors">
                    <X size={12} className="text-slate-400" />
                  </button>
                )}
              </div>
              <button onClick={() => setShowAdvanced(!showAdvanced)} className={`flex items-center gap-1.5 px-3 py-[7px] rounded-lg text-[13px] border transition-all ${showAdvanced ? 'bg-[#232F3E] border-[#232F3E] text-white' : 'bg-white border-[#D5D9D9] text-[#565959] hover:border-[#888]'}`}>
                <Filter size={13} />More
              </button>
              <div className="flex-1" />
              <span className="text-[12px] text-[#565959] tabular-nums">{filteredSellers.length} of {pagination.totalElements}</span>
              <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
                <button onClick={() => setView('card')} className={`p-1.5 rounded-md transition-all ${viewMode === 'card' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400 hover:text-slate-600'}`} title="Card view"><LayoutGrid size={14} /></button>
                <button onClick={() => setView('table')} className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400 hover:text-slate-600'}`} title="Table view"><List size={14} /></button>
              </div>
            </div>
            {showAdvanced && (
              <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center gap-2.5 bg-slate-50/50">
                <input type="text" value={filters.pan} onChange={e => handleFilterChange('pan', e.target.value.toUpperCase())} placeholder="PAN" maxLength={10} className={`${advInputCls} font-mono tracking-wide w-[120px]`} />
                <input type="text" value={filters.gstin} onChange={e => handleFilterChange('gstin', e.target.value.toUpperCase())} placeholder="GSTIN" maxLength={15} className={`${advInputCls} font-mono tracking-wide w-[160px]`} />
                <input type="tel" value={filters.contactPhone} onChange={e => handleFilterChange('contactPhone', e.target.value)} placeholder="Phone" className={`${advInputCls} w-[140px]`} />
                <input type="email" value={filters.contactEmail} onChange={e => handleFilterChange('contactEmail', e.target.value)} placeholder="Email" className={`${advInputCls} w-[200px]`} />
              </div>
            )}
          </div>
        )}

        {/* ━━━ ACTIVE FILTER CHIPS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2">
            {quickFilter && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-[12px] font-medium rounded-md border border-[#FFD89E]">
                {quickFilter === 'active' && 'Active Sellers'}
                {quickFilter === 'connected' && 'Connected Channels'}
                {quickFilter === 'disconnected' && 'Needs Attention'}
                {quickFilter === 'no_channels' && 'No Channels'}
                <button onClick={() => setQuickFilter('')} className="ml-0.5 hover:text-[#0F1111]"><X size={12} /></button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-[12px] font-medium rounded-md border border-[#FFD89E]">
                Status: {filters.status}
                <button onClick={() => handleFilterChange('status', '')} className="ml-0.5 hover:text-[#0F1111]"><X size={12} /></button>
              </span>
            )}
            {filters.channels.length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-[12px] font-medium rounded-md border border-[#FFD89E]">
                Marketplaces: {filters.channels.length}
                <button onClick={() => handleFilterChange('channels', [])} className="ml-0.5 hover:text-[#0F1111]"><X size={12} /></button>
              </span>
            )}
            {filters.notificationConfig.length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-[12px] font-medium rounded-md border border-[#FFD89E]">
                Notifications: {filters.notificationConfig.length}
                <button onClick={() => handleFilterChange('notificationConfig', [])} className="ml-0.5 hover:text-[#0F1111]"><X size={12} /></button>
              </span>
            )}
            {filters.search && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-[12px] font-medium rounded-md border border-[#FFD89E]">
                Search: "{filters.search}"
                <button onClick={() => handleFilterChange('search', '')} className="ml-0.5 hover:text-[#0F1111]"><X size={12} /></button>
              </span>
            )}
            {filters.accountManager && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-[12px] font-medium rounded-md border border-[#FFD89E]">
                Account Manager: "{filters.accountManager}"
                <button onClick={() => handleFilterChange('accountManager', '')} className="ml-0.5 hover:text-[#0F1111]"><X size={12} /></button>
              </span>
            )}
            {filters.amUnassigned && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 text-[12px] font-medium rounded-md border border-amber-200">
                Account Manager: Unassigned
                <button onClick={() => setFilters(f => ({ ...f, amUnassigned: false }))} className="ml-0.5 hover:text-amber-900"><X size={12} /></button>
              </span>
            )}
            {filters.notifEnabledType && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] text-[12px] font-medium rounded-md border border-[#FFD89E]">
                {(NOTIF_TYPE_LABELS[filters.notifEnabledType] || filters.notifEnabledType)} enabled
                <button onClick={() => setFilters(f => ({ ...f, notifEnabledType: '' }))} className="ml-0.5 hover:text-[#0F1111]"><X size={12} /></button>
              </span>
            )}
            {filters.notifDisabledType && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 text-[12px] font-medium rounded-md border border-amber-200">
                {(NOTIF_TYPE_LABELS[filters.notifDisabledType] || filters.notifDisabledType)} not enabled
                <button onClick={() => setFilters(f => ({ ...f, notifDisabledType: '' }))} className="ml-0.5 hover:text-amber-900"><X size={12} /></button>
              </span>
            )}
            <button onClick={handleResetFilters} className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-semibold text-[#B12704] hover:text-red-700 hover:bg-red-50 rounded-md transition-colors">
              <X size={13} /> Clear All
            </button>
          </div>
        )}

        {/* ━━━ CONTENT ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {loading ? (
          viewMode === 'card' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {[...Array(6)].map((_, i) => <CardSkeleton key={i} />)}
            </div>
          ) : <TableSkeleton />
        ) : sellers.length === 0 && !hasActiveFilters ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-20 h-20 rounded-3xl bg-slate-100 flex items-center justify-center mb-5"><Store size={36} className="text-slate-300" /></div>
            <h3 className="text-lg font-semibold text-slate-800 tracking-[-0.01em]">No sellers yet</h3>
            <p className="text-[13px] text-slate-500 mt-1.5 max-w-xs text-center">Add your first seller to start managing their marketplace connections and notifications.</p>
            <button onClick={() => setAddSellerModal({ open: true, editData: null })} className="mt-5 h-10 inline-flex items-center gap-2 px-5 bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] text-[#0F1111] rounded-xl text-sm font-semibold hover:from-[#f0c14b] hover:to-[#e7ae00] transition-all shadow-sm">
              <Plus size={16} /> Add Your First Seller
            </button>
          </div>
        ) : (sellers.length === 0 || filteredSellers.length === 0) ? (
          <div className="bg-white rounded-lg border border-[#D5D9D9] py-16">
            <div className="flex flex-col items-center justify-center">
              <Search size={32} className="text-[#D5D9D9] mb-3" />
              <h3 className="text-[14px] font-semibold text-[#0F1111]">No sellers match your filters</h3>
              <p className="text-[13px] text-[#565959] mt-1">Try adjusting your search or filter criteria</p>
            </div>
          </div>
        ) : viewMode === 'card' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filteredSellers.map((seller, idx) => (
              <SellerCard
                key={seller.id}
                seller={seller}
                onSelect={() => handleSelectSeller(seller)}
                onEdit={() => setAddSellerModal({ open: true, editData: seller })}
                onDelete={handleDeleteSeller}
                onConnectChannel={handleConnectChannel}
                onRefresh={refetch}
                onAssignUsers={() => { setSelectedSeller({ ...seller }); navigate('/settings/seller-info?tab=team'); }}
                style={{ animationDelay: `${idx * 40}ms` }}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-[#D5D9D9] overflow-hidden overflow-x-auto">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="bg-[#F7F7F7] border-b border-[#D5D9D9]">
                    <th className="text-left text-[11px] font-semibold text-[#565959] uppercase tracking-wider py-2.5 px-5">
                      <SortableTh label="Seller" col="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSortClick} />
                    </th>
                    <th className="text-left text-[11px] font-semibold text-[#565959] uppercase tracking-wider py-2.5 px-4">Marketplaces</th>
                    <th className="text-left text-[11px] font-semibold text-[#565959] uppercase tracking-wider py-2.5 px-4">Channels</th>
                    <th className="text-left text-[11px] font-semibold text-[#565959] uppercase tracking-wider py-2.5 px-4">Status</th>
                    <th className="text-left text-[11px] font-semibold text-[#565959] uppercase tracking-wider py-2.5 px-4">Account Manager</th>
                    <th className="text-left text-[11px] font-semibold text-[#565959] uppercase tracking-wider py-2.5 px-4">Contact</th>
                    <th className="text-left text-[11px] font-semibold text-[#565959] uppercase tracking-wider py-2.5 px-4">
                      <SortableTh label="Created" col="createdAt" sortBy={sortBy} sortDir={sortDir} onSort={handleSortClick} />
                    </th>
                    <th className="py-2.5 px-3 w-[50px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSellers.map(seller => (
                    <SellerTableRow
                      key={seller.id}
                      seller={seller}
                      onSelect={() => handleSelectSeller(seller)}
                      onEdit={() => setAddSellerModal({ open: true, editData: seller })}
                      onDelete={handleDeleteSeller}
                      onConnectChannel={handleConnectChannel}
                      onRefresh={refetch}
                      onAssignUsers={() => { setSelectedSeller({ ...seller }); navigate('/settings/seller-info?tab=team'); }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      </div>

      {/* ━━━ FOOTER BAR ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <FooterBar
        totalPages={pagination.totalPages}
        currentPage={currentPage + 1}
        onPageChange={(p) => setCurrentPage(p - 1)}
        totalItems={pagination.totalElements}
        pageSize={PAGE_SIZE}
      />
      {/* ━━━ MODALS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <AddSellerModal isOpen={addSellerModal.open} onClose={() => setAddSellerModal({ open: false, editData: null })} onSave={handleSaveSeller} editData={addSellerModal.editData} />
      <ConnectPlatformPicker isOpen={platformPicker.open} onClose={() => setPlatformPicker({ open: false, sellerId: null })} onSelect={handlePlatformSelect} />
      <AmazonConnectModal isOpen={amazonModal.open} onClose={() => setAmazonModal({ open: false, sellerId: null })} onConnect={handleConnect} sellerId={amazonModal.sellerId} />
      <FlipkartConnectModal isOpen={flipkartModal.open} onClose={() => setFlipkartModal({ open: false, sellerId: null })} onConnect={handleConnect} sellerId={flipkartModal.sellerId} />
      <MeeshoConnectModal isOpen={meeshoModal.open} onClose={() => setMeeshoModal({ open: false, sellerId: null })} onConnect={handleConnect} sellerId={meeshoModal.sellerId} />
      <ConfirmModal isOpen={confirmModal.open} onClose={() => setConfirmModal({ open: false, title: '', message: '', variant: 'warning', action: null })} onConfirm={handleConfirm} title={confirmModal.title} message={confirmModal.message} variant={confirmModal.variant} loading={confirmLoading} />
      <AccountManagerStatsModal
        isOpen={amStatsOpen}
        onClose={() => setAmStatsOpen(false)}
        onFilter={(name) => { setFilters(f => ({ ...f, accountManager: name, amUnassigned: false })); setAmStatsOpen(false); }}
        onFilterUnassigned={() => { setFilters(f => ({ ...f, accountManager: '', amUnassigned: true })); setAmStatsOpen(false); }}
      />
      <NotificationCoverageModal
        isOpen={notifCoverageOpen}
        onClose={() => setNotifCoverageOpen(false)}
        onFilterEnabled={(type) => { setFilters(f => ({ ...f, notifEnabledType: type, notifDisabledType: '' })); setNotifCoverageOpen(false); }}
        onFilterDisabled={(type) => { setFilters(f => ({ ...f, notifDisabledType: type, notifEnabledType: '' })); setNotifCoverageOpen(false); }}
      />
    </AdminLayout>
  );
}